import { useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import Card from "../components/Card"

type ResetResponse = {
  ok?: boolean
  error?: string
}

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token") || ""

  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isComplete, setIsComplete] = useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    if (!token) {
      setError("Missing reset token.")
      return
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.")
      return
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.")
      return
    }

    setIsSubmitting(true)
    try {
      const response = await fetch("/api/auth/password-reset-confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      })
      const data = (await response.json().catch(() => ({}))) as ResetResponse
      if (!response.ok) {
        setError(data.error ?? "Failed to reset password.")
        return
      }
      setIsComplete(true)
    } catch {
      setError("Unable to reach the auth service.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-sand px-6">
      <Card className="w-full max-w-md">
        <p className="text-xl font-semibold text-slate-900">Reset password</p>
        {isComplete ? (
          <div className="mt-4 space-y-3 text-sm">
            <p className="text-moss">Password updated successfully.</p>
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="w-full rounded-full bg-moss px-4 py-2 font-semibold text-white"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm text-stone">Set a new password for your account.</p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4 text-sm">
              <input
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                placeholder="New password"
                className="w-full rounded-full border border-line bg-white px-4 py-2"
              />
              <input
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Confirm new password"
                className="w-full rounded-full border border-line bg-white px-4 py-2"
              />
              {error ? <p className="text-xs text-rose-600">{error}</p> : null}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full bg-moss px-4 py-2 font-semibold text-white"
              >
                {isSubmitting ? "Updating..." : "Update password"}
              </button>
            </form>
            <p className="mt-4 text-xs text-stone">
              Remembered it? <Link to="/login" className="underline">Sign in</Link>
            </p>
          </>
        )}
      </Card>
    </div>
  )
}

export default ResetPasswordPage
